'use client';

import { Button, FlexContainer } from '../../components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';

import TableColumnHeader from '../TableColumnHeader';

import type { ReactNode } from 'react';
import type { ActionColumnOptions, ColumnFactory } from './types';

/**
 * Factory function to create an action column with buttons
 *
 * @example
 * ```tsx
 * const ACTIONS_COLUMN = createActionColumn<ApiKey>({
 *   label: 'Actions',
 *   options: {
 *     actions: [
 *       {
 *         label: 'Delete',
 *         onClick: (row) => handleDelete(row),
 *         variant: 'destructive',
 *         size: 'sm'
 *       }
 *     ]
 *   }
 * });
 * ```
 */
export function createActionColumn<TData>(config: {
  id?: string;
  label: string;
  options: ActionColumnOptions<TData>;
}): ColumnFactory<TData> {
  const { id = 'actions', label, options } = config;
  const { size = 150, actions, className, meta } = options;

  const columnHelper = createColumnHelper<TData>();

  return columnHelper.display({
    id,
    size,
    meta: {
      ...meta,
      style: {
        textAlign: 'right',
        ...meta?.style,
      },
    },
    header: ({ table }) => <TableColumnHeader accessor={id} label={label} tableState={table.getState()} />,
    cell: ({ row }) => {
      const rowData = row.original;

      return (
        <FlexContainer className={`gap-2 ${className || ''}`} items="center" justify="end">
          {actions.map((action, index) => {
            const isDisabled = typeof action.disabled === 'function' ? action.disabled(rowData) : action.disabled;
            const buttonLabel = typeof action.label === 'function' ? action.label(rowData) : action.label;

            return (
              <Button
                disabled={isDisabled}
                key={index}
                onClick={() => action.onClick(rowData)}
                size={action.size || 'sm'}
                variant={action.variant || 'primary'}
              >
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {(action.icon ? <FontAwesomeIcon icon={action.icon as any} /> : null) as ReactNode}
                {buttonLabel}
              </Button>
            );
          })}
        </FlexContainer>
      );
    },
  });
}
